/**
 * Textos de `agency` en español — la fuente. `en/agency.ts` tiene que tener
 * exactamente las mismas claves (lo exige el tipo), así que una clave nueva
 * acá rompe el typecheck hasta que alguien la traduce.
 *
 * Interpolación con funciones, no con plantillas "{n}": el compilador revisa
 * los argumentos y cada idioma arma su propia gramática (plurales, orden).
 */
const agency = {
  /** Encabezado común de las pantallas de la consola. */
  scope: 'Consola de agencia',
  publishPosition: 'Publicar vacante',
  publishFirstPosition: 'Publicar mi primera vacante',
  seeMyPositions: 'Ver mis vacantes',
  backToDashboard: 'Volver a mi panel',
  page: (n: number) => `Página ${n}`,
  /** El vacío de una etapa del embudo; `label` es el nombre de la pestaña. */
  emptyStage: (label: string) => `Nadie en ${label.toLowerCase()} todavía`,

  layout: {
    loading: 'Cargando tu agencia',
    brandSuffix: 'Agencias',
    activeAgency: 'Agencia activa',
    navLabel: 'Secciones de la agencia',
    nav: {
      overview: 'Resumen',
      positions: 'Vacantes',
      candidates: 'Candidatos',
      team: 'Equipo',
    },
    /** La agencia existe pero TBit la suspendió. */
    suspended: {
      title: 'Tu agencia está suspendida',
      body: 'Para revisar el acceso, escríbenos desde tu cuenta.',
    },
    loadError: {
      title: 'No pudimos cargar tu agencia',
      retry: 'Reintentar',
      empty: 'Respuesta vacía del servidor.',
    },
  },

  overview: {
    loading: 'Cargando el resumen',
    title: 'Resumen',
    lede: 'Dónde está parado tu proceso hoy.',
    pendingNote: 'Los candidatos sugeridos se muestran cuando tu acceso esté aprobado.',
    stats: {
      openPositions: 'Vacantes abiertas',
      seeAll: 'Ver todas',
      suggestedNew: 'Nuevos sugeridos',
      since: (date: string) => `Desde el ${date}`,
      shortlisted: 'En shortlist',
      awaiting: 'Esperando respuesta',
      awaitingDesc: 'Invitaciones enviadas sin contestar',
      accepted: 'Aceptaron',
      acceptedDesc: 'Puedes escribirles',
    },
    empty: {
      title: 'Todavía no tienes vacantes abiertas',
      note: 'Publica una y buscamos candidatos en nuestra base apenas la guardes.',
    },
    nextStep: {
      scope: 'Siguiente paso',
      titleAccepted: 'Escríbele a quien aceptó',
      titleReview: 'Revisa a los sugeridos',
      seeAllCandidates: 'Ver todos los candidatos',
      accepted:
        'Hay candidatos que aceptaron que los contactes. Su correo está en la ficha, en la pestaña Aceptados de cada vacante.',
      shortlisted: 'Ya tienes gente en shortlist. Pídeles el contacto y les mandamos la invitación por ti.',
      none: 'Entra a una vacante y preselecciona a quienes te sirvan. Nadie se entera hasta que pidas su contacto.',
    },
    howItWorks: {
      scope: 'Cómo funciona',
      title: 'El candidato decide',
      foot: 'Nunca verás un correo que no te hayan autorizado.',
      body: 'Los perfiles se ven sin datos personales. Cuando pides el contacto, le llega una invitación a la persona; sus datos aparecen sólo si acepta.',
    },
  },

  positions: {
    title: 'Vacantes',
    lede: 'Cada vacante busca candidatos sola. Acá ves en qué punto va cada una.',
    search: 'Buscar cargo o ubicación',
    filterByStatus: 'Filtrar por estado',
    filter: {
      all: 'Todas',
      open: 'Abiertas',
      paused: 'En pausa',
      filled: 'Cubiertas',
    },
    columns: {
      title: 'Cargo',
      location: 'Ubicación',
      wage: 'Salario',
      status: 'Estado',
      suggested: 'Sugeridos',
      shortlisted: 'Shortlist',
      contacted: 'Contactados',
      accepted: 'Aceptados',
      created: 'Creada',
    },
    seeCandidates: 'Ver candidatos',
    searchCandidates: 'Buscar candidatos',
    noMatch: {
      title: 'Ninguna vacante coincide con ese filtro',
      note: 'Prueba con otro cargo, otra ubicación u otro estado.',
    },
    empty: {
      title: 'Todavía no publicas ninguna vacante',
      note: 'Publica la primera y te sugerimos candidatos de nuestra base.',
    },
  },

  form: {
    titleEdit: 'Editar vacante',
    titleNew: 'Nueva vacante',
    cancel: 'Cancelar',
    required: 'El cargo, la ubicación y la descripción son obligatorios.',
    savedRefreshed: 'Cambios guardados. Volvimos a buscar candidatos con la nueva descripción.',
    saved: 'Cambios guardados.',
    title: 'Cargo *',
    titlePlaceholder: 'Ej: Operario de cosecha',
    company: 'Empresa',
    companyPlaceholder: 'Si lo dejas vacío usamos el nombre de tu agencia',
    location: 'Ubicación *',
    locationPlaceholder: 'Ej: Miami, FL',
    status: 'Estado',
    jobType: 'Tipo de empleo',
    jobTypeUnset: 'Sin especificar',
    jobTypeHint: 'Si lo dejas sin especificar, no descartamos a nadie por este campo.',
    description: 'Descripción *',
    descriptionPlaceholder: 'Qué hace la persona en el día a día, en qué tipo de empresa, con qué equipo.',
    descriptionHint: 'Con este texto buscamos candidatos: entre más concreto, mejores los perfiles.',
    requirements: 'Requisitos',
    requirementsPlaceholder: 'Experiencia, licencias, idiomas.',
    duties: 'Funciones',
    dutiesPlaceholder: 'Tareas concretas del puesto.',
    wageFrom: 'Salario desde',
    wageTo: 'Hasta',
    wagePer: 'Por',
    wagePerOptions: {
      Hour: 'Hora',
      Week: 'Semana',
      Month: 'Mes',
      Year: 'Año',
    },
    workersNeeded: 'Vacantes',
    beginDate: 'Inicio',
    endDate: 'Fin',
    housing: 'Incluye alojamiento',
    contactEmail: 'Correo de contacto de la vacante',
    contactEmailPlaceholder: 'Si lo dejas vacío usamos el correo de tu agencia',
    contactEmailHint: 'Es tu correo, no el del candidato: a él lo contactas siempre por la plataforma.',
    saveChanges: 'Guardar cambios',
  },

  detail: {
    loading: 'Cargando la vacante',
    notFound: {
      title: 'No encontramos esta vacante',
      note: 'Puede que se haya eliminado, o que pertenezca a otra de tus agencias.',
    },
    back: '← Vacantes',
    edit: 'Editar',
    searchCandidates: 'Buscar candidatos',
    searching: 'Buscando candidatos',
    found: (n: number) => `${n} candidato(s) encontrados.`,
    noneFound: 'Todavía no hay candidatos que encajen con esta vacante.',
    needsEmbedding:
      'Todavía no podemos buscar candidatos para esta vacante: nos falta procesar su descripción. Edítala y guarda de nuevo para reintentarlo.',
    stagesLabel: 'Etapas del proceso',
    contactVisible: 'Estas personas aceptaron que las contactes. Su correo es visible sólo acá.',
    card: {
      scope: 'La vacante',
      title: 'Detalle',
      description: 'Descripción',
      requirements: 'Requisitos',
      duties: 'Funciones',
      summary: (workers: string, begin: string, end: string) =>
        `Vacantes: ${workers} · Inicio: ${begin} · Fin: ${end}`,
      housing: 'Incluye alojamiento',
    },
  },

  /** Lo que pasa al mover un candidato; compartido por el detalle y Candidatos. */
  actions: {
    invited: 'Le enviamos la invitación al candidato. Si acepta, verás sus datos de contacto en Aceptados.',
    conflict: 'Ese candidato ya cambió de estado. Actualizamos la lista.',
    inviteFailed: 'No pudimos enviarle la invitación. Intenta de nuevo en un momento.',
  },

  candidates: {
    title: 'Candidatos',
    lede: 'Todos tus candidatos, de todas tus vacantes, en el estado en que están.',
    pendingLede: 'Tus candidatos se habilitan después de aprobar el acceso.',
    filterLabel: 'Estado del candidato',
    loading: 'Cargando candidatos',
    contactVisible: 'Estas personas aceptaron que las contactes: su correo está en la ficha.',
  },

  /** Alta autoservicio: quien entra sin puesto crea su agencia acá. */
  create: {
    title: 'Crea tu agencia o empresa',
    lede: 'Publica tu primera vacante desde tu propia consola.',
    name: 'Nombre *',
    country: 'País',
    countryOther: 'Otro',
    /** Por código ISO: el código es lo que se envía, esto es lo que se lee. */
    countries: {
      US: 'Estados Unidos',
      MX: 'México',
      CO: 'Colombia',
      GT: 'Guatemala',
      HN: 'Honduras',
      SV: 'El Salvador',
      NI: 'Nicaragua',
      CR: 'Costa Rica',
      PA: 'Panamá',
      DO: 'República Dominicana',
      PE: 'Perú',
      EC: 'Ecuador',
      CL: 'Chile',
      AR: 'Argentina',
      BR: 'Brasil',
      ES: 'España',
    },
    website: 'Sitio web (opcional)',
    contactEmail: 'Correo de contacto (opcional)',
    note: 'Podrás publicar vacantes de inmediato. Ver candidatos requiere aprobación, sin costo.',
    submitting: 'Creando…',
    submit: 'Crear agencia',
  },

  /**
   * La casilla OBLIGATORIA de los términos, al crear la agencia y al pedir
   * acceso a candidatos si todavía no aceptó la versión vigente. Las agencias
   * no tienen términos propios: aceptan los Términos y condiciones de TBit Jobs.
   */
  terms: {
    before: 'Acepto los ',
    label: 'Términos y condiciones de TBit Jobs',
    href: 'https://tbit.app/es/legal/tbit-jobs-terminos-y-condiciones',
    after: '.',
    required: 'Marca la casilla para aceptar los términos y continuar.',
    outdated: 'Actualizamos los términos. Léelos y vuelve a marcar la casilla.',
  },

  /** El acceso a candidatos de una agencia en PENDING (aviso y compuerta). */
  access: {
    requested: (date: string) =>
      `Solicitud enviada el ${date}. Te avisamos por correo cuando esté aprobada.`,
    intro:
      'Ya puedes publicar vacantes. Para ver candidatos, solicita acceso: lo aprueba una persona de TBit, no tiene costo y es por seguridad de los candidatos.',
    askAdmin: 'Para solicitarlo, pídeselo al administrador de tu agencia.',
    pendingCount: (n: number) =>
      `${n} candidato${n === 1 ? '' : 's'} sugerido${n === 1 ? '' : 's'} se mostrarán cuando tu acceso esté aprobado.`,
    sending: 'Enviando…',
    request: 'Solicitar acceso a candidatos',
    gateTitle: 'El acceso a candidatos requiere aprobación',
  },

  team: {
    loading: 'Cargando el equipo',
    title: 'Equipo',
    lede: 'Quién puede entrar a esta consola y qué puede hacer.',
    invalidEmail: 'Escribe un correo válido.',
    added: (email: string) =>
      `Listo. ${email} ya tiene un puesto: entra con ese correo desde la página de acceso de TBit. No le mandamos ningún correo.`,
    removed: (email: string) => `${email} ya no tiene acceso a esta agencia.`,
    membersTitle: 'Personas con acceso',
    seats: (n: number) => `${n} puesto(s)`,
    empty: {
      title: 'Todavía no hay nadie más',
      note: 'Agrega a quien trabaje contigo las vacantes. Entran con su propio correo.',
    },
    columns: {
      person: 'Persona',
      role: 'Permiso',
      status: 'Estado',
      since: 'Desde',
    },
    you: ' · tú',
    active: 'Activo',
    notYet: 'Sin entrar',
    lastAdmin: 'La agencia tiene que quedarse con al menos un administrador',
    remove: 'Quitar',
    add: {
      scope: 'Agregar',
      title: 'Dale acceso a alguien más',
      foot: 'No enviamos correo de invitación: la persona entra a TBit con este mismo correo y ya encuentra la consola.',
      email: 'Correo *',
      emailPlaceholder: 'persona@tuagencia.com',
      name: 'Nombre',
      namePlaceholder: 'Opcional',
      role: 'Permiso',
      submit: 'Agregar al equipo',
    },
    recruiterNote:
      'Solo quien administra la agencia puede agregar o quitar gente. Si necesitas sumar a alguien, pídeselo.',
    roles: {
      admin: 'Administra',
      recruiter: 'Recluta',
    },
    roleNotes: {
      admin: 'Publica vacantes, ve candidatos y maneja el equipo.',
      recruiter: 'Publica vacantes y ve candidatos. No puede agregar ni quitar gente.',
    },
    errors: {
      invite403: 'Solo un administrador puede agregar gente al equipo.',
      invite409: 'Ese correo ya tiene un puesto en esta agencia.',
      invite400: 'Revisa el correo y el rol: alguno no es válido.',
      inviteGeneric: 'No pudimos agregar a esa persona. Intenta de nuevo.',
      remove403: 'Solo un administrador puede quitar gente del equipo.',
      remove409:
        'La agencia tiene que quedarse con al menos un administrador. Asciende a alguien más antes de quitar a esta persona.',
      remove404: 'Ese puesto ya no existe.',
      removeGeneric: 'No pudimos quitar a esa persona. Intenta de nuevo.',
    },
  },

  matchCard: {
    positionDeleted: 'Vacante eliminada',
    experience: (years: number) => `${years} año(s) de experiencia`,
    affinity: 'afinidad',
    lookingFor: (roles: string) => `Busca: ${roles}`,
    in: (places: string) => `En: ${places}`,
    invitedOn: (date: string) =>
      `Le enviamos tu invitación el ${date}. Te avisamos aquí cuando responda.`,
    declined: 'El candidato prefirió no compartir sus datos para esta vacante.',
    accepted: 'Aceptó que lo contactes',
    shortlist: 'Preseleccionar',
    requestContact: 'Solicitar contacto',
    dismiss: 'Descartar',
  },

  /** Vocabulario cerrado del backend → lo que lee la persona. */
  jobTypes: {
    full_time: 'Tiempo completo',
    part_time: 'Medio tiempo',
    seasonal: 'De temporada',
    temporary: 'Temporal',
    contract: 'Por contrato',
  },
  matchStatus: {
    suggested: 'Sugerido',
    shortlisted: 'En preselección',
    contact_requested: 'Invitación enviada',
    candidate_accepted: 'Aceptó el contacto',
    candidate_declined: 'No quiso el contacto',
    dismissed: 'Descartado',
  },
  positionStatus: {
    open: 'Abierta',
    paused: 'En pausa',
    filled: 'Cubierta',
  },
  /** Las seis pestañas del embudo: nombre y qué hacer para que se llene. */
  pipeline: {
    suggested: {
      label: 'Sugeridos',
      hint: 'Busca candidatos y aparecerán acá los perfiles que más se parecen a la vacante. Solo mostramos personas que autorizaron aparecer en la bolsa de talento.',
    },
    shortlisted: {
      label: 'Shortlist',
      hint: 'Preselecciona desde Sugeridos a quienes quieras revisar con calma. Nadie se entera de que lo preseleccionaste.',
    },
    contact_requested: {
      label: 'Contactados',
      hint: 'Cuando pidas el contacto de alguien de la shortlist, le enviamos la invitación y la verás acá mientras responde.',
    },
    candidate_accepted: {
      label: 'Aceptados',
      hint: 'Acá aparecen quienes aceptaron que los contactes, con su correo. Nadie llega solo: primero hay que invitarlos desde Contactados.',
    },
    candidate_declined: {
      label: 'No aceptaron',
      hint: 'Nadie ha rechazado tu invitación. Si alguien lo hace, aparece acá y no vuelve a las etapas.',
    },
    dismissed: {
      label: 'Descartados',
      hint: 'Lo que descartes queda acá por si te arrepientes: desde la ficha puedes volver a preseleccionarlo.',
    },
  },
  unnamedCandidate: 'Candidato sin nombre en el CV',
  candidateRef: (tail: string) => `Candidato #${tail}`,
  candidate: 'Candidato',
};

export default agency;
